import React, { useState, useRef, useEffect, useId } from 'react';
import { ChevronDown } from './Icons';
import './CustomSelect.css';

/**
 * Polished theme-aware dropdown that replaces native <select>.
 *
 * options: Array<{
 *   value    : string,
 *   label    : string,
 *   subtitle?: string,   — shown below the label (e.g. client name)
 *   badge?   : string,   — small right-side badge (e.g. "No PDF")
 *   badgeWarn? : bool,   — amber badge colour
 *   color?   : string,   — coloured left dot (e.g. for status options)
 *   disabled?: bool,
 * }>
 */
export function CustomSelect({
  id,
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  disabled = false,
  className = '',
  compact = false,        // tighter padding (used for inline status selects)
  dropUp = false,         // open upward instead of down
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const uid = useId();
  const dropId = `cs-drop-${uid}`;

  const selected = options.find(o => o.value === value);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function out(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', out);
    return () => document.removeEventListener('mousedown', out);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function esc(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [open]);

  function toggle() { if (!disabled) setOpen(v => !v); }
  function pick(opt) {
    if (opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
  }

  return (
    <div
      ref={containerRef}
      id={id}
      className={[
        'cs-wrap',
        className,
        open     ? 'cs-open'     : '',
        disabled ? 'cs-disabled' : '',
        compact  ? 'cs-compact'  : '',
        dropUp   ? 'cs-drop-up'  : '',
      ].filter(Boolean).join(' ')}
    >
      {/* Trigger */}
      <button
        type="button"
        className="cs-trigger"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={dropId}
        disabled={disabled}
      >
        <span className="cs-trigger-inner">
          {selected?.color && (
            <span className="cs-dot" style={{ background: selected.color }} />
          )}
          <span className="cs-trigger-text">
            <span className={`cs-value ${!selected ? 'cs-placeholder' : ''}`}>
              {selected ? selected.label : placeholder}
            </span>
            {selected?.subtitle && (
              <span className="cs-trigger-sub">{selected.subtitle}</span>
            )}
          </span>
          {selected?.badge && (
            <span className={`cs-trigger-badge ${selected.badgeWarn ? 'cs-badge-warn' : ''}`}>
              {selected.badge}
            </span>
          )}
        </span>
        <ChevronDown size={13} className="cs-arrow" aria-hidden="true" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="cs-dropdown" id={dropId} role="listbox">
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={opt.value === value}
              className={[
                'cs-option',
                opt.value === value ? 'cs-option--selected' : '',
                opt.disabled       ? 'cs-option--disabled' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => pick(opt)}
              disabled={opt.disabled}
            >
              <span className="cs-opt-inner">
                {opt.color && (
                  <span className="cs-dot" style={{ background: opt.color }} />
                )}
                <span className="cs-opt-text">
                  <span className="cs-opt-label">{opt.label}</span>
                  {opt.subtitle && (
                    <span className="cs-opt-sub">{opt.subtitle}</span>
                  )}
                </span>
                {opt.badge && (
                  <span
                    className={`cs-opt-badge ${opt.badgeWarn ? 'cs-badge-warn' : ''}`}
                  >
                    {opt.badge}
                  </span>
                )}
              </span>
              {opt.value === value && (
                <span className="cs-check" aria-hidden="true" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
